import { makeStyles } from "tss-react/mui";
import { MiSkeleton, MiStackedTypography } from "@miplatform/components";
import {
  DATE_TIME_FORMATS,
  ORDER_STATUS,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { IOrder } from "@miplatform/types";
import { getFormattedDate } from "@miplatform/utils";
import { EntityStatusListItemView } from "@mibuildweb/components";
import { Typography } from "@mui/material";
import { Box } from "@mui/system";

export function HomePopperContent({
  home,
  orders,
  currentOrder,
  startDate,
  completeDate,
  phaseText,
  color,
  closeMenu,
}: HomePopperContentProps) {
  const { navigate } = useRouter();

  const { classes } = useStyles({ color });
  const handleOrderNavigate = (
    event: React.MouseEvent<HTMLDivElement>,
    order: any
  ) => {
    event.preventDefault();
    event.stopPropagation();
    navigate(`/homes/${home.id}/orders?orderId=${order.id}`);
    closeMenu();
  };

  return (
    <Box className={classes.popperContent}>
      <Box className={classes.popperHeader}>
        <Typography
          variant={TYPOGRAPHY_VARIANTS.CAPTION}
          className={classes.headerText}
        >
          {phaseText}
        </Typography>
      </Box>

      <Box className={classes.addressSection}>
        <Typography variant={TYPOGRAPHY_VARIANTS.HD2}>
          {home?.address?.addressLine1 || " "}
        </Typography>
        <Typography className={classes.addressDetails}>
          {home
            ? `${home?.address?.city || ""} | ${home?.community || ""} | ${
                home?.builder || ""
              }`
            : ""}
        </Typography>
      </Box>

      <Box className={classes.dateSection}>
        <Box className={classes.dateContainer}>
          <MiStackedTypography
            title="Start Date"
            description={
              startDate
                ? getFormattedDate(DATE_TIME_FORMATS.FORMAT_M_D_YY, startDate)
                : "-"
            }
            descriptionProps={{ fontWeight: "bold" }}
          />
          <MiStackedTypography
            title="Complete Date"
            description={
              completeDate
                ? getFormattedDate(
                    DATE_TIME_FORMATS.FORMAT_M_D_YY,
                    completeDate
                  )
                : "-"
            }
            descriptionProps={{ fontWeight: "bold" }}
          />
          <MiStackedTypography
            title="Current Order"
            description={currentOrder?.orderTemplateName || "-"}
            descriptionProps={{ fontWeight: "bold" }}
          />
        </Box>
      </Box>
      <Box>
        {orders ? (
          <>
            <Box className={classes.ordersHeader}>
              <Typography
                variant={TYPOGRAPHY_VARIANTS.CAPTION}
                fontWeight={600}
              >
                Orders
              </Typography>
              <Typography
                variant={TYPOGRAPHY_VARIANTS.CAPTION}
                fontWeight={600}
              >
                {
                  orders.filter((o) => o.orderStatus === ORDER_STATUS.COMPLETE)
                    .length
                }
                /{orders.length}
              </Typography>
            </Box>
            <Box className={classes.ordersWrapper}>
              <Box className={classes.ordersList}>
                {orders.map((order) => (
                  <EntityStatusListItemView
                    key={order.id}
                    entity={order}
                    getDisplayName={(order) => order.orderTemplateName || "-"}
                    getDetail={(order) => {
                      const completedDate = order.completeDate
                        ? getFormattedDate(
                            DATE_TIME_FORMATS.FORMAT_M_D_YY,
                            new Date(order.completeDate)
                          )
                        : "--/--/--";
                      return `Complete: ${completedDate}`;
                    }}
                    getStatus={(order) => order.orderStatus}
                    onEntityClick={handleOrderNavigate}
                  />
                ))}
              </Box>
            </Box>
          </>
        ) : (
          <Box>
            <MiSkeleton height={100} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

type colorType = "success" | "error" | "primary" | "secondary";

type HomePopperContentProps = {
  home: {
    address?: { addressLine1?: string; city?: string };
    community?: string;
    builder?: string;
    id: string;
  };
  orders: IOrder[] | null;
  currentOrder: any;
  startDate: Date | null;
  completeDate: Date | null;
  phaseText: string;
  color: string;
  closeMenu: () => void;
};

const useStyles = makeStyles<{ color: string }>()((theme, { color }) => ({
  popperContent: {
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(2),
    minWidth: 288,
    boxShadow: theme.shadows[4],
  },
  popperSection: {
    marginBottom: theme.spacing(1.5),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  popperHeader: {
    height: 24,
    paddingLeft: theme.spacing(1),
    borderTopRightRadius: theme.spacing(1.5),
    borderTopLeftRadius: theme.spacing(1.5),
    backgroundColor: theme.palette[color as colorType].main,
  },
  headerText: {
    color: theme.palette.common.white,
  },
  addressSection: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
  addressDetails: {
    color: theme.palette.grey.medium,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.fontWeight.regular,
  },
  dateSection: {
    display: "flex",
    gap: theme.spacing(4),
    backgroundColor: theme.palette.grey.mediumLight,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  dateContainer: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  ordersHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 2),
  },
  ordersWrapper: {
    backgroundColor: theme.palette.common.white,
    borderBottomLeftRadius: theme.spacing(2),
    borderBottomRightRadius: theme.spacing(2),
  },
  ordersList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2, 2, 2),
    maxHeight: 200,
    overflowY: "auto",
    borderBottomLeftRadius: theme.spacing(2),
    borderBottomRightRadius: theme.spacing(2),
  },
  orderItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey.mediumLight,
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.grey.accent}`,
    cursor: "pointer",
  },
  orderName: {
    fontSize: theme.typography.HD2.fontSize,
    fontWeight: theme.fontWeight.semiBold,
  },
  orderDate: {
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.grey.medium,
    fontWeight: theme.fontWeight.regular,
  },
  statusBadge: {
    padding: theme.spacing(0.5, 1.5),
    borderRadius: theme.spacing(1),
    whiteSpace: "nowrap",
  },
  statusText: {
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.common.white,
    fontWeight: theme.fontWeight.regular,
  },
}));
